import { Button, Title } from "@mantine/core";
import { IconTrash, IconUpload } from "@tabler/icons-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { showErrorNotification } from "../notifications";

export interface Props {
  accept?: string;
  onFileSelect: (file: File | null) => void;
}

const SelectFile = ({ accept, onFileSelect }: Props) => {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>();
  const [isDragging, setIsDragging] = useState(false);

  const isValidFileType = (file: File) => {
    if (!accept) return true;

    const acceptedTypes = accept.split(",").map((type) => type.trim());
    return acceptedTypes.some((type) => {
      if (type.startsWith(".")) {
        return file.name.toLowerCase().endsWith(type.toLowerCase());
      }
      return file.type === type;
    });
  };

  const handleFileSelected = (selectedFile: File) => {
    if (isValidFileType(selectedFile)) {
      setFile(selectedFile);
      onFileSelect(selectedFile);
    } else {
      showErrorNotification({
        message: t("selectFile.invalidFileType"),
      });
      setFile(undefined);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      handleFileSelected(selectedFile);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      handleFileSelected(droppedFile);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDragEnter = () => {
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  return (
    <div
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      className={`w-full h-full rounded-lg relative p-2 lg:p-6 border-dashed transition-all duration-200 flex flex-col items-center justify-center gap-4
        ${isDragging ? "bg-blue-50 border-blue-300" : "bg-gray-50 border-gray-300"} border-2 hover:bg-gray-100
      `}
    >
      <IconUpload size={44} color="gray" />
      <Button
        variant="outline"
        radius="xl"
        onClick={() => document.getElementById("fileInput")?.click()}
      >
        {t("selectFile.selectFile")}
      </Button>
      <span className="text-gray-500">{t("selectFile.dragAndDrop")}</span>

      <div className="w-full absolute bottom-0">
        {file && (
          <div className="w-full overflow-hiddenflex flex-col  font-medium">
            <div className="w-full bg-gray-200 flex items-center justify-between px-4 py-1">
              <Title order={6}>{t("selectFile.file")}</Title>
              <Button
                variant="subtle"
                color="red.5"
                size="xs"
                radius={"xl"}
                rightSection={<IconTrash size={16} />}
                onClick={() => {
                  setFile(null);
                  onFileSelect(null);
                }}
              >
                {t("selectFile.removeFile")}
              </Button>
            </div>
            <div className="px-4 py-2">
              <span className="text-sm text-gray-700">{file.name}</span>
            </div>
          </div>
        )}
      </div>

      <input
        id="fileInput"
        type="file"
        accept={accept}
        className="hidden"
        onChange={handleFileSelect}
      />
    </div>
  );
};

export default SelectFile;
